import * as Sentry from '@sentry/react'
import { observer } from 'mobx-react'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { DealerLayout, DealerSwitcherContext } from '@/components/layout/DealerLayout'
import { NavBar } from '@/components/layout/NavBar'
import {
  areaForPath,
  dealerIdFromPath,
  pathForDealer,
} from '@/components/layout/NavBar/dealerScope'
import { PrivateRoute } from '@/components/routing/PrivateRoute'
import { PublicRoute } from '@/components/routing/PublicRoute'
import { AnnouncementBanner } from '@/components/shared/AnnouncementBanner'
import { AnnouncementDetailModal } from '@/components/shared/AnnouncementDetailModal'
import { ImpersonationBanner } from '@/components/shared/ImpersonationBanner'
import { SentryFeedback } from '@/components/shared/SentryFeedback'
import { TaskProgress } from '@/components/shared/TaskProgress'
import { Analytics } from '@/pages/Analytics'
import { Billing } from '@/pages/Billing'
import { BillingDetails } from '@/pages/BillingDetails'
import { Dashboard } from '@/pages/Dashboard'
import { DealerDetails } from '@/pages/DealerDetails'
import { Login } from '@/pages/Login'
import { MultiFactorAuthentication } from '@/pages/MultiFactorAuthentication'
import { NotFound } from '@/pages/NotFound'
import { PasswordReset } from '@/pages/PasswordReset'
import { PasswordResetRequest } from '@/pages/PasswordResetRequest'
import { PrivacyPolicy } from '@/pages/PrivacyPolicy'
import { RepairOrder } from '@/pages/RepairOrder'
import { RepairOrders } from '@/pages/RepairOrders'
import { LogRepairOrders } from '@/pages/RoIntake/LogRepairOrders'
import { AllDealersWorkload, RoStatusDashboard } from '@/pages/RoStatusDashboard'
import { Settings } from '@/pages/Settings'
import { TermsAndConditions } from '@/pages/TermsAndConditions'
import { UserProfile } from '@/pages/UserProfile'
import { Users } from '@/pages/Users'
import { WorkQueue } from '@/pages/WorkQueue'
import { managedTypeFilter, WORK_QUEUE_CHANGED } from '@/pages/WorkQueue/queue'
import { getDealers } from '@/services/dealers.service'
import { getWorkQueueCounts } from '@/services/repairOrders.service'
import { BillingFilterStoreContext } from '@/stores/BillingFilterStore'
import { DashboardStoreContext } from '@/stores/DashboardStore'
import { DealerFilterStoreContext } from '@/stores/DealerFilterStore'
import { GlobalSettingsContext } from '@/stores/GlobalSettingsStore'
import { RootStoreContext } from '@/stores/RootStore'
import { UserStoreContext } from '@/stores/UserStore'
import { clearDealerGroup } from '@/utils/analytics'
import { HOME_PATH, lastDealerStorageKey, readLastDealerId } from '@/utils/landing'

/**
 *
 * Routes
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */
// Log ROs moved out from under /dealer/:id/intake to sit beside the status dashboard.
// The old address keeps working so links already shared still land.
const LegacyLogRosRedirect = () => {
  const { id } = useParams()
  return <Navigate replace to={`/ro-intake/${id}/log`} />
}

// The RO Claims view was retired on 2026-09-28: the dashboard holds its numbers and
// Manage Repair Order its per-RO fields. Its address lands on the dealer's dashboard.
const RoClaimsRedirect = () => {
  const { id } = useParams()
  return <Navigate replace to={`/ro-intake/${id}`} />
}

const AnalyticsRedirect = () => <Navigate replace to="/trends" />

// The Ready Queue became the Work Queue's Ready tab (WARP-959). Its old addresses land
// there, so bookmarks and shared links keep working.
const ReadyQueueRedirect = () => {
  const { dealerId } = useParams()
  return <Navigate replace to={dealerId ? `/work-queue/${dealerId}` : '/work-queue'} />
}

const BillingRedirect = () => {
  const { dealerId } = useParams()
  return <Navigate to={`/dealer/${dealerId}/billing`} replace />
}

const PageRoutes = observer(() => {
  // Context
  const location = useLocation()
  const { clearStore } = useContext(RootStoreContext)
  const {
    isAuthenticated,
    isWpcUser,
    hasValidProfile,
    user: currentUser,
  } = useContext(UserStoreContext)
  const dealerFilterStore = useContext(DealerFilterStoreContext)
  const billingFilterStore = useContext(BillingFilterStoreContext)
  const { isGlobalRoListEnabled, isAnalyticsEnabled } = useContext(GlobalSettingsContext)
  const { isWpcExecutive } = useContext(UserStoreContext)
  const { type } = useContext(DashboardStoreContext)
  const navigate = useNavigate()

  // The dealer list behind the nav selector. Fetched here rather than inside NavBar:
  // NavBar sits very early in the module graph, and pulling a service into it drags
  // the services/instance <-> stores/RootStore import cycle forward far enough that
  // RootStore's class fields evaluate before UserStore has initialised.
  const [navDealerOptions, setNavDealerOptions] = useState([])

  useEffect(() => {
    if (!isAuthenticated) return undefined
    const controller = new AbortController()

    const typeOfDealers = isWpcExecutive
      ? `?${type === 'wpc' ? 'is_dealer_managed=false' : 'is_dealer_managed=true'}`
      : ''

    getDealers(
      `/dealers/all${typeOfDealers}`,
      () => {},
      () => {},
      controller.signal,
    ).then((dealers) => {
      if (!dealers || controller.signal.aborted) return
      setNavDealerOptions(
        dealers.map((d) => ({ id: d.id, label: d.name, isRoIntakeEnabled: d.isRoIntakeEnabled })),
      )
    })

    return () => controller.abort()
  }, [isAuthenticated, isWpcExecutive, type])

  // The last dealer this user worked on. The dealer items in the nav with no all-dealers
  // view (Log ROs, Outstanding List) go back to this one from a page with no dealer in
  // its address; the Dashboard and Work Queue open every dealer instead (WARP-960).
  // Remembered per user in localStorage; every access is guarded, since storage can
  // throw (private mode).
  const lastDealerKey = currentUser?.id ? lastDealerStorageKey(currentUser.id) : null
  const [rememberedDealerId, setRememberedDealerId] = useState(null)

  useEffect(() => {
    if (!lastDealerKey) return
    setRememberedDealerId(readLastDealerId(currentUser.id))
  }, [lastDealerKey])

  useEffect(() => {
    const dealerId = dealerIdFromPath(location.pathname)
    if (!dealerId || !lastDealerKey || dealerId === rememberedDealerId) return
    setRememberedDealerId(dealerId)
    try {
      window.localStorage.setItem(lastDealerKey, dealerId)
    } catch {
      // Losing the memory only costs a click on the dealer switcher.
    }
  }, [location.pathname, lastDealerKey])

  // Only a dealer this user can still reach, and only once the list says so: a
  // remembered dealer they have since lost access to is simply forgotten.
  const lastDealerId =
    rememberedDealerId && navDealerOptions.some((option) => option.id === rememberedDealerId)
      ? rememberedDealerId
      : null

  // The dealer the nav's dealer items open from a page with no dealer in its address: the
  // last one used, else the first RO Intake dealer, else the first dealer. The same order
  // bare /ro-intake picks in, so the nav and the landing page agree. Without it, a new
  // session, or a last dealer outside the WPC/Dealer Managed list now shown, left every
  // dealer item disabled.
  const navDealerId =
    lastDealerId ||
    navDealerOptions.find((option) => option.isRoIntakeEnabled)?.id ||
    navDealerOptions[0]?.id ||
    null

  // The Work Queue badge: every RO to work (Ready, Closed, and Admin Review), and how
  // many are past the 24-hour SLA, for the same scope the nav link opens, so the badge
  // and the page it leads to always agree: the dealer in the address, else every dealer
  // (WARP-960), under an executive's WPC / Dealer Managed switch. Recounted when that
  // scope changes, when the queue page reports a save, and every five minutes, since ROs
  // cross the SLA untouched.
  const [workQueueBadge, setWorkQueueBadge] = useState(null)
  const badgeDealerId = dealerIdFromPath(location.pathname)

  useEffect(() => {
    if (!isAuthenticated) return undefined
    const controller = new AbortController()

    const recount = () =>
      getWorkQueueCounts(
        badgeDealerId,
        controller.signal,
        managedTypeFilter({ dealerId: badgeDealerId, isWpcExecutive, type }),
      ).then((counts) => {
        if (counts && !controller.signal.aborted) {
          setWorkQueueBadge({ count: counts.total || 0, late: counts.late || 0 })
        }
      })

    recount()
    const timer = setInterval(recount, 5 * 60 * 1000)
    window.addEventListener(WORK_QUEUE_CHANGED, recount)

    return () => {
      controller.abort()
      clearInterval(timer)
      window.removeEventListener(WORK_QUEUE_CHANGED, recount)
    }
  }, [isAuthenticated, badgeDealerId, isWpcExecutive, type])

  useEffect(() => {
    // If the user is authenticated, but we don't have a valid profile stored we
    // need to reset local storage and force log them out.
    if (isAuthenticated && !hasValidProfile) {
      clearStore()
    }
  }, [isAuthenticated, hasValidProfile])

  // Attach the Replay integration only after authentication so the
  // DOM-wrapping surface it installs (rrweb) is absent from the
  // unauthenticated pages implicated in WARP-890. `SentryFeedback` adds the
  // Feedback integration itself, because its own effect runs before this one
  // (WARP-956).
  useEffect(() => {
    if (!isAuthenticated) return
    if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') return

    const client = Sentry.getClient()
    if (!client) return

    if (
      import.meta.env.VITE_SENTRY_REPLAYS_ENABLED === 'true' &&
      !client.getIntegrationByName('Replay')
    ) {
      Sentry.addIntegration(
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      )
    }
  }, [isAuthenticated])

  // Clear filter stores and analytics dealer group when navigating between
  // sections so events on unrelated screens don't inherit a previous
  // dealer's context.
  useEffect(() => {
    if (!isAuthenticated) return
    const path = location.pathname
    if (!path.startsWith('/billing') && !path.endsWith('/billing'))
      billingFilterStore.clearContext()
    if (!path.startsWith('/dealer/')) dealerFilterStore.clearContext()
    const isDealerScopedRoute = path.startsWith('/dealer/') || path.startsWith('/billing/')
    if (!isDealerScopedRoute) clearDealerGroup()
  }, [location.pathname])

  const options = useMemo(() => {
    // WARP-920 / 2026-09-17: the nav is split by what the dealer selector means.
    //
    //   scope: 'dealer' — the selector drives it. `dealerPath` is where it goes with a
    //                     dealer chosen, `path` is its all-dealers list view.
    //   scope: 'global' — cross-dealer, ignores the selector, lives in the More menu.
    //
    // An area with no `path` has no list view, so it is unreachable until a dealer is
    // picked. Log ROs is the only one: you cannot log a repair order against nobody.
    const defaultOptions = [
      // Dashboard leads: it is the first page after login (Mark and Michelle,
      // 2026-09-28), so it is first on the bar, then Log ROs, then the Ready Queue.
      {
        label: 'Dashboard',
        // Its all-dealers view is the cross-dealer workload (WARP-960): every dealer's
        // Work Queue counts, by dealer or by person. So from a page with no dealer, and
        // from "All dealers" in the switcher, it opens that.
        path: '/ro-intake',
        dealerPath: (id) => `/ro-intake/${id}`,
        scope: 'dealer',
        // The new views show only to RO Intake dealers (Mark, 2026-09-24): "if they see
        // it and can't use it [we'll] get a million questions."
        requiresRoIntake: true,
      },
      {
        label: 'Log ROs',
        path: null,
        dealerPath: (id) => `/ro-intake/${id}/log`,
        scope: 'dealer',
        // Only a dealer with RO Intake turned on can log ROs. NavBar hides the item
        // for any other dealer rather than linking to a page that has to refuse.
        requiresRoIntake: true,
      },
      // WARP-959: the work queue (the Ready Queue, WARP-951, grown to a tab per open
      // status). For WPC and dealers alike since 2026-10-06; the badge is the count to
      // work, red when any is late.
      {
        label: 'Work Queue',
        // Every dealer's queue with no dealer picked (WARP-960), as the Dashboard's
        // all-dealers counts link to it. The badge counts the same scope as the link.
        path: '/work-queue',
        dealerPath: (id) => `/work-queue/${id}`,
        scope: 'dealer',
        requiresRoIntake: true,
        badge: workQueueBadge,
      },
      // The dealer's Outstanding List. Always one dealer's list, never the all-dealers
      // one: with no dealer picked it waits for one, like Log ROs. The list of every
      // dealer is "Dealers", under More.
      {
        label: 'Outstanding List',
        path: null,
        dealerPath: (id) => `/dealer/${id}/detail`,
        // One RO's page is part of the list it came from. A dealer switch from there
        // lands on the new dealer's list, since the RO belongs to the old dealer.
        alsoMatches: (id) => [`/dealer/${id}/repair-order`],
        scope: 'dealer',
        // Where a dealer switch lands when the current area cannot take the new dealer:
        // an RO Intake page, for a dealer without RO Intake. Every dealer has this one.
        isDealerHome: true,
      },
    ]

    // Every dealer this user can reach, with their Outstanding List numbers — the page
    // that used to be "Dashboard", and that the Outstanding List nav item used to open
    // when no dealer was picked. Its own item now, so that item means one thing.
    defaultOptions.push({ label: 'Dealers', path: '/dashboard', scope: 'global' })

    // Global RO search is behind a backend feature flag. Deliberately not WPC-only —
    // Michelle on 2026-09-17: "I think that would be a great tool for even our dealers
    // to have." The endpoint already scopes results with RepairOrder.objects.for_user.

    if (isGlobalRoListEnabled) {
      defaultOptions.push({ label: 'RO Search', path: '/repair-orders', scope: 'global' })
    }

    // Trends (the Performance Explorer; the code still calls it Analytics) is behind
    // its own backend feature flag.
    if (isAnalyticsEnabled) {
      defaultOptions.push({ label: 'Trends', path: '/trends', scope: 'global' })
    }

    if (isWpcUser) {
      defaultOptions.push({
        label: 'Users',
        path: '/users',
        scope: 'global',
      })
    }
    if (isWpcUser) {
      defaultOptions.push({
        label: 'Billing',
        // The all-dealers billing list is WPC Managed only; it sends you home in Dealer
        // Managed mode. One dealer's billing is open to every dealer, self-managed ones
        // too, as the dealer header's Billing tab was before these tabs moved into the
        // nav. So in Dealer Managed mode the item waits for a dealer, like Log ROs.
        path: type === 'wpc' ? '/billing' : null,
        dealerPath: (id) => `/dealer/${id}/billing`,
        scope: 'dealer',
      })
    }

    return defaultOptions
  }, [isWpcUser, isGlobalRoListEnabled, isAnalyticsEnabled, workQueueBadge, type])

  // The dealer switcher in each page's header band (WARP-920). Picking a dealer keeps
  // you in the area you are in, for the new dealer. The exception is the RO Intake pages
  // (Log ROs, Dashboard, Ready Queue), which a dealer without RO Intake does not get: that
  // switch lands on the dealer's Outstanding List (`isDealerHome`) instead.
  const dealerSwitcher = useMemo(() => {
    const typedOptions = options.filter((o) => !o.type || o.type === type)
    const area = areaForPath(location.pathname, typedOptions)

    return {
      dealerOptions: navDealerOptions,
      // "All dealers" only where the area has an all-dealers view of its own: the
      // Dashboard and the Work Queue (WARP-960), and Billing.
      canShowAll: Boolean(area?.scope === 'dealer' && area.path && !area.isDealerHome),
      // In an RO Intake area, only a dealer with RO Intake opens the area's page; the rest
      // open their Outstanding List (below). The list tags the RO Intake dealers, so
      // anyone can tell which is which before picking one.
      marksRoIntake: Boolean(area?.requiresRoIntake),
      onSwitch: (dealerId) => {
        const picked = navDealerOptions.find((option) => option.id === dealerId)
        if (area?.requiresRoIntake && picked && !picked.isRoIntakeEnabled) {
          const home = typedOptions.find((option) => option.isDealerHome)
          if (home) navigate(home.dealerPath(dealerId))
          return
        }
        const next = pathForDealer(location.pathname, typedOptions, dealerId)
        if (next) navigate(next)
      },
    }
  }, [navDealerOptions, options, type, location.pathname, navigate])

  return (
    // Signed in, a column: the nav and any banners take what they need, and the page fills
    // the rest. The page used to be a fixed 100vh - 64px (the nav alone), so an
    // announcement or impersonation banner pushed its bottom off screen on every page
    // (2026-10-01). Signed out, a plain block as before: in a column, the sign-in pages'
    // `h-full` cards stretched to the bottom of the screen (2026-10-12).
    <div
      className={mergeClassNames(
        'bg-background h-screen w-screen',
        isAuthenticated && 'flex flex-col',
      )}
    >
      <Helmet titleTemplate="%s - WARP" defaultTitle="Warranty Processing Portal">
        <meta name="description" content="Warranty Processing Portal" />
      </Helmet>

      <NavBar
        dealerOptions={navDealerOptions}
        fallbackDealerId={navDealerId}
        options={options}
        orientation="horizontal"
      />

      {isAuthenticated && <ImpersonationBanner />}
      {isAuthenticated && <AnnouncementBanner />}
      {isAuthenticated && <AnnouncementDetailModal />}

      <div
        className={mergeClassNames(
          isAuthenticated
            ? 'bg-background flex min-h-0 grow flex-col'
            : 'bg-background min-h-[calc(100vh-200px)] md:min-h-[calc(100vh-200px)] lg:min-h-[calc(100vh-290px)]',
        )}
      >
        <DealerSwitcherContext.Provider value={dealerSwitcher}>
          <Routes>
            {/* Main Routes */}
            <Route path="*" element={<NotFound />} />
            <Route element={<PrivacyPolicy />} caseSensitive path="/privacy-policy" />
            <Route element={<TermsAndConditions />} caseSensitive path="/terms-and-conditions" />

            {/* Public Routes */}
            <Route element={<PublicRoute component={Login} />} caseSensitive path="/" />
            <Route element={<PublicRoute component={Login} />} caseSensitive path="/login" />
            <Route
              element={<PublicRoute component={MultiFactorAuthentication} />}
              caseSensitive
              path="/verification"
            />
            <Route
              element={<PublicRoute component={PasswordReset} />}
              caseSensitive
              path="/password-reset"
            />
            <Route
              element={<PublicRoute component={PasswordReset} />}
              caseSensitive
              path="/set-password"
            />
            <Route
              element={<PublicRoute component={PasswordResetRequest} />}
              caseSensitive
              path="/password-reset-request"
            />

            <Route element={<Navigate replace to={HOME_PATH} />} caseSensitive path="/" />

            {/* Private Routes */}
            <Route
              element={<PrivateRoute component={Dashboard} />}
              caseSensitive
              path="/dashboard"
            />

            <Route
              element={<PrivateRoute component={RepairOrders} />}
              caseSensitive
              path="/repair-orders"
            />

            <Route element={<PrivateRoute component={Analytics} />} caseSensitive path="/trends" />
            {/* The page's address before it was renamed Trends. Kept for bookmarks. */}
            <Route caseSensitive path="/analytics" element={<AnalyticsRedirect />} />

            <Route
              caseSensitive
              path="/profile"
              element={<PrivateRoute component={UserProfile} />}
            />
            <Route
              caseSensitive
              path="/settings"
              element={<PrivateRoute component={Settings} />}
            />

            {/* WARP-960 — the Dashboard with All dealers picked: where the work is, by
              dealer and by person. Also where login lands; it goes straight to the dealer
              for a user with only one. */}
            <Route
              caseSensitive
              path="/ro-intake"
              element={<PrivateRoute component={AllDealersWorkload} />}
            />
            {/* WARP-943 — one dealer's dashboard: status buckets across five periods, every
              number opening the list behind it. */}
            <Route
              caseSensitive
              path="/ro-intake/:dealerId"
              element={<PrivateRoute component={RoStatusDashboard} />}
            />
            <Route
              caseSensitive
              path="/ro-intake/:dealerId/log"
              element={<PrivateRoute component={LogRepairOrders} />}
            />
            {/* WARP-959 — the work queue, for every dealer or one. Dealers too: the API
                scopes it to the ROs each user can see. */}
            {['/work-queue', '/work-queue/:dealerId'].map((path) => (
              <Route
                caseSensitive
                element={<PrivateRoute component={WorkQueue} />}
                key={path}
                path={path}
              />
            ))}
            {['/ready-queue', '/ready-queue/:dealerId'].map((path) => (
              <Route caseSensitive element={<ReadyQueueRedirect />} key={path} path={path} />
            ))}
            {/* The prototype's old address, kept so links shared with WPC during the
              2026-09-17 review still land somewhere. */}
            <Route
              caseSensitive
              path="/demo/ro-status"
              element={<Navigate replace to="/ro-intake" />}
            />

            <Route path="/dealer/:id" element={<PrivateRoute component={DealerLayout} />}>
              <Route path="detail" element={<DealerDetails />} />
              <Route path="intake" element={<RoClaimsRedirect />} />
              <Route path="intake/log" element={<LegacyLogRosRedirect />} />
              <Route path="billing" element={<BillingDetails />} />
            </Route>
            <Route
              path="/dealer/:dealerId/repair-order/create"
              element={
                <PrivateRoute
                  component={RepairOrder}
                  // Mirrors the backend RepairOrder "add" perm (user_can_manage_ros): WPC
                  // users or dealer admins. The self-managed-dealer gate lives on the "Add
                  // Repair Order" button (canManageDraftRos), the only entry to this route;
                  // RO Intake creates via its own Log ROs screen, not here.
                  restrictionCheck={(user) =>
                    user.userType === 'WPC User' ||
                    user.dealerUserProfile?.role === 'Administrator'
                  }
                  skipLayout
                  skipFooter
                />
              }
            />
            <Route
              path="/dealer/:dealerId/repair-order/:id"
              element={<PrivateRoute component={RepairOrder} skipLayout skipFooter />}
            />
            <Route
              path="/users"
              element={
                <PrivateRoute
                  component={Users}
                  restrictionCheck={(user) => user.userType === 'WPC User'}
                />
              }
            />
            {/* WARP-939 renamed this page. Keep the old path working for bookmarks. */}
            <Route path="/dealer-users" element={<Navigate replace to="/users" />} />

            <>
              <Route
                path="/billing"
                element={
                  <PrivateRoute
                    component={Billing}
                    restrictionCheck={(user) => user.userType === 'WPC User'}
                  />
                }
              />
              <Route path="/billing/:dealerId/detail" element={<BillingRedirect />} />
            </>
          </Routes>
        </DealerSwitcherContext.Provider>
      </div>

      {isAuthenticated && import.meta.env.VITE_SENTRY_FEEDBACK_ENABLED === 'true' && (
        <SentryFeedback />
      )}
      <TaskProgress />
    </div>
  )
})

export default PageRoutes
